import { useSuspenseQuery } from '@apollo/client';
import classNames from 'classnames';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ErrorBoundary } from '@sorare/error-boundary';

import {
  So5LeaderboardType,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Marquee } from '@sorare/core/src/atoms/container/Marquee';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import {
  PvEImplementation,
  TITLE,
} from '@sorare/core/src/components/Minigames/PvEImplementation';
import { Ambassadors } from '@sorare/core/src/components/landing/Ambassadors';
import { CommunityBlock } from '@sorare/core/src/components/landing/CommunityBlock';
import { Footer } from '@sorare/core/src/components/landing/Footer';
import { HeroVideo } from '@sorare/core/src/components/landing/HeroVideo';
import { League, Leagues } from '@sorare/core/src/components/landing/Leagues';
import { Section } from '@sorare/core/src/components/landing/Section';
import { StepByStep } from '@sorare/core/src/components/landing/StepByStep';
import {
  CountryFlag,
  Testimonials,
} from '@sorare/core/src/components/landing/Testimonials';
import { Pictures } from '@sorare/core/src/components/landing/types';
import { FOOTBALL } from '@sorare/core/src/constants/__generated__/routes';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { isType } from '@sorare/core/src/gql';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

// FOR MINIGAME
import { FAQ } from 'components/landing/shared/FAQ';
import {
  POSITIONS_MAP,
  PVE_QUERY,
  buildData,
  // eslint-disable-next-line import/no-restricted-paths
} from 'pages/minigames/pve_/$league/page';

import { PLAYERS } from './PLAYERS';
import footerBackgroundAvif from './assets/footer/background.avif';
import footerBackground from './assets/footer/background.jpg';
import heroVideoWebm from './assets/heroVideo/heroVideo.av1.webm';
import heroVideoH264 from './assets/heroVideo/heroVideo.h264.mp4';
import heroVideoVp9 from './assets/heroVideo/heroVideo.vp9.webm';
import newLandingPoster from './assets/heroVideo/newLandingPoster.jpg';
import popupBackground from './assets/minigame/background.png';
import scoutNumberAvif from './assets/whyPlay/1.avif';
import scoutNumber from './assets/whyPlay/1.png';
import playNumberAvif from './assets/whyPlay/2.avif';
import playNumber from './assets/whyPlay/2.png';
import winNumberAvif from './assets/whyPlay/3.avif';
import winNumber from './assets/whyPlay/3.png';
import WhyPlayPlayAvif from './assets/whyPlay/WhyPlayPlay.avif';
import WhyPlayPlay from './assets/whyPlay/WhyPlayPlay.png';
import WhyPlayScoutAvif from './assets/whyPlay/WhyPlayScout.avif';
import WhyPlayScout from './assets/whyPlay/WhyPlayScout.png';
import WhyPlayWinAvif from './assets/whyPlay/WhyPlayWin.avif';
import WhyPlayWin from './assets/whyPlay/WhyPlayWin.png';
import bigWinBackground from './assets/winBig/background.png';
import europeanTicketsAvif from './assets/winBig/europeanTickets.avif';
import europeanTickets from './assets/winBig/europeanTickets.jpg';
import flyWithMilanAvif from './assets/winBig/flyWithMilan.avif';
import flyWithMilan from './assets/winBig/flyWithMilan.jpg';
import goldenTicketsAvif from './assets/winBig/goldenTickets.avif';
import goldenTickets from './assets/winBig/goldenTickets.jpg';
import signedJerseysAvif from './assets/winBig/signedJerseys.avif';
import signedJerseys from './assets/winBig/signedJerseys.jpg';
import zidaneJerseysAvif from './assets/winBig/zidaneJerseys.avif';
import zidaneJerseys from './assets/winBig/zidaneJerseys.jpg';

const Root = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--quadruple-unit);
  overflow: hidden;
  position: relative;
  isolation: isolate;
  padding-bottom: var(--double-unit);
  @media ${tabletAndAbove} {
    gap: calc(10 * var(--unit));
  }
`;

const Hero = styled.div`
  position: relative;
  isolation: isolate;
  &.showOtherLeagues {
    @media ${tabletAndAbove} {
      margin-bottom: 100px;
    }
  }
`;

const MarqueeLeagueContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const MarqueeLeagueImage = styled(Img)`
  border-radius: var(--double-unit);
`;

const MarqueeLeagueImageChip = styled(Chip)`
  margin: var(--double-unit) var(--double-unit);
  position: absolute;
  z-index: 2;
`;

const WhyPlayContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: calc(10 * var(--unit));
  @media ${tabletAndAbove} {
    flex-direction: row;
  }
`;

const WinBigSection = styled(Section)`
  z-index: 6;
  position: relative;
`;

const WinBigBackground = styled(Img)`
  position: absolute;
  z-index: 0;
  margin-top: 250px;
`;

const MarqueeContent = styled(Horizontal).attrs({ gap: 4 })`
  height: fit-content;
  padding: var(--quadruple-unit) 8px;
`;

const MarqueeWinImage = styled(Img)`
  height: 375px;
  box-shadow: 0 0 24px 8px var(--c-black);
  border-radius: var(--double-unit);
`;

const LeaguesContainer = styled.div`
  margin-top: calc(-10 * var(--unit));
  position: absolute;
  z-index: 2;
`;

const leaguesAssets = [
  {
    name: 'u23',
    id: 'u23',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-under_twenty_one/picture.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-under_twenty_one/header.svg',
  },
  {
    name: 'LALIGA',
    id: 'seasonal-spain',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-spain/picture-in_season.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-spain/header.svg',
  },
  {
    name: 'Premier League',
    id: 'seasonal-england',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-england/picture-in_season.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-england/header.svg',
  },
  {
    name: 'Bundesliga',
    id: 'seasonal-germany',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-germany/picture-in_season.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-germany/header.svg',
  },
  {
    name: 'Champions league',
    id: 'seasonal-champions',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-champions/picture-in_season.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-champions/header.svg',
  },
];

const steps = [
  {
    img: {
      avif: WhyPlayScoutAvif,
      pictureUrl: WhyPlayScout,
      height: 333,
      width: 322,
    },
    titleImg: {
      avif: scoutNumberAvif,
      pictureUrl: scoutNumber,
      height: 46,
      width: 46,
    },
    title: (
      <FormattedMessage
        id="landing.NewLanding.whyPlay.title1"
        defaultMessage="Scout"
      />
    ),
    subtitle: (
      <FormattedMessage
        id="landing.NewLanding.whyPlay.subtitle1"
        defaultMessage="Buy, trade & sell player cards"
      />
    ),
  },
  {
    img: {
      avif: WhyPlayPlayAvif,
      pictureUrl: WhyPlayPlay,
      height: 326,
      width: 302,
    },
    titleImg: {
      avif: playNumberAvif,
      pictureUrl: playNumber,
      height: 88,
      width: 88,
    },
    title: (
      <FormattedMessage
        id="landing.NewLanding.whyPlay.title2"
        defaultMessage="Play"
      />
    ),
    subtitle: (
      <FormattedMessage
        id="landing.NewLanding.whyPlay.subtitle2"
        defaultMessage="Competitions every Tuesday and Friday"
      />
    ),
  },
  {
    img: {
      avif: WhyPlayWinAvif,
      pictureUrl: WhyPlayWin,
      height: 248,
      width: 345,
    },
    titleImg: {
      avif: winNumber,
      pictureUrl: winNumberAvif,
      height: 88,
      width: 88,
    },
    title: (
      <FormattedMessage
        id="landing.NewLanding.whyPlay.title3"
        defaultMessage="Win"
      />
    ),
    subtitle: (
      <FormattedMessage
        id="landing.NewLanding.whyPlay.subtitle3"
        defaultMessage="Incredible prizes"
      />
    ),
  },
];

const marqueeContent: Pictures[] = [
  { avif: europeanTicketsAvif, pictureUrl: europeanTickets },
  { avif: flyWithMilanAvif, pictureUrl: flyWithMilan },
  { avif: goldenTicketsAvif, pictureUrl: goldenTickets },
  { avif: signedJerseysAvif, pictureUrl: signedJerseys },
  { avif: zidaneJerseysAvif, pictureUrl: zidaneJerseys },
];

const countryFlags: Record<string, CountryFlag> = {
  de: { name: 'de', slug: 'de' },
  es: { name: 'es', slug: 'es' },
  fr: { name: 'fr', slug: 'fr' },
  gb: { name: 'gb', slug: 'gb' },
  nl: { name: 'nl', slug: 'nl' },
  us: { name: 'us', slug: 'us' },
};

const testimonies = [
  {
    heid: 'e6d75e725ba37282fd7ec1e644679e8f',
    slug: 'oliv36',
    country: countryFlags.gb,
    rating: 5,
    message: (
      <FormattedMessage
        id="landing.NewLanding.testimonies.1"
        defaultMessage="Great way to prove your football knowledge."
      />
    ),
  },
  {
    heid: 'ba328652bddc386364b650594df62e17',
    slug: 'TP92',
    country: countryFlags.fr,
    rating: 5,
    message: (
      <FormattedMessage
        id="landing.NewLanding.testimonies.2"
        defaultMessage="Sorare takes your love for football to the next level."
      />
    ),
  },
  {
    heid: 'ba328652bddc386364b650594df62e17',
    slug: 'Le Flaco SG',
    country: countryFlags.de,
    rating: 4,
    message: (
      <FormattedMessage
        id="landing.NewLanding.testimonies.3"
        defaultMessage="Quality game, real life prizes, simple, and fun!"
      />
    ),
  },
  {
    heid: 'fdb3762caadadb87a08328ec0eb9deb5',
    slug: 'lud015',
    country: countryFlags.nl,
    rating: 5,
    message: (
      <FormattedMessage
        id="landing.NewLanding.testimonies.4"
        defaultMessage="The future of fantasy sports!"
      />
    ),
  },
  {
    heid: '5879a5dbbd07eaa8068b6b0d3895fc91',
    slug: 'tsardinho',
    country: countryFlags.us,
    rating: 5,
    message: (
      <FormattedMessage
        id="landing.NewLanding.testimonies.5"
        defaultMessage="Enhances every aspect of being a football fan."
      />
    ),
  },
];

type Props = {
  leagues?: League[];
  leaderboards: So5LeaderboardType[];
  showFAQ?: boolean;
  variant:
    | 'Football'
    | 'Bundesliga_landing'
    | 'LaLigaEA_landing'
    | 'EPL_launch'
    | 'SerieA_landing';
};

type PveWidgetProps = {
  handleNavigate: () => void;
};

const PveWidget = ({ handleNavigate }: PveWidgetProps) => {
  const { data } = useSuspenseQuery(PVE_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      slugs: PLAYERS,
    },
  });

  const playersData = buildData(
    data.anyCards
      .filter(card => isType(card, 'Card'))
      .filter(e => e.score && e.score > 0)
  );
  return (
    <ErrorBoundary fallback={null}>
      <div style={{ width: '100%' }}>
        <PvEImplementation
          fullHeight
          key="football"
          title={TITLE.FOOTBALL_PREMIER_LEAGUE}
          playersData={playersData}
          onClick={handleNavigate}
          fieldUrl="https://frontend-assets.sorare.com/football/so5_league/seasonal-england/stadium-in_season.jpg"
          positionsMap={POSITIONS_MAP}
          popupBackground={popupBackground}
        />
      </div>
    </ErrorBoundary>
  );
};

const NewLanding = ({ leagues, leaderboards, showFAQ, variant }: Props) => {
  const { signUp } = useConnectionContext();
  const { currentUser } = useCurrentUserContext();
  const navigate = useNavigate();
  const handleNavigate = () => (currentUser ? navigate(FOOTBALL) : signUp());

  const track = useEvents();
  const isLoggedIn = useIsLoggedIn();

  useEffect(() => {
    if (!isLoggedIn) {
      track('View Homepage Disconnected', {
        landing_variant: variant,
      });
    }
  }, [isLoggedIn, track, variant]);

  return (
    <Root>
      <Hero className={classNames({ showOtherLeagues: !!leagues })}>
        <HeroVideo
          sources={[
            {
              src: heroVideoWebm,
              type: 'video/webm; codecs="av01.0.31M.08,opus"',
            },
            { src: heroVideoVp9, type: 'video/webm; codecs="vp9"' },
            { src: heroVideoH264 },
          ]}
          videoPoster={newLandingPoster}
          title={
            <FormattedMessage
              id="landing.NewLanding.HeroVideo.title"
              defaultMessage="Fantasy Football Reinvented"
            />
          }
          subtitle={
            <FormattedMessage
              id="landing.NewLanding.HeroVideo.subtitle"
              defaultMessage="This is fantasy football but more global, more in-depth and more rewarding."
            />
          }
        />
        {leagues && (
          <LeaguesContainer>
            <Leagues leagues={leagues} />
          </LeaguesContainer>
        )}
      </Hero>
      <Marquee gap={4}>
        {leaguesAssets.map(league => (
          <MarqueeLeagueContainer key={league.id}>
            <MarqueeLeagueImageChip size="large" color="transparent">
              {league.name}
            </MarqueeLeagueImageChip>
            <MarqueeLeagueImage
              src={league.pictureUrl}
              fetchpriority="low"
              alt=""
              width="324"
              height="564"
            />
          </MarqueeLeagueContainer>
        ))}
      </Marquee>
      <Section
        title={
          <FormattedMessage
            id="landing.NewLanding.whyPlay.title"
            defaultMessage="Think you know fantasy football?"
          />
        }
        subtitle={
          <FormattedMessage
            id="landing.NewLanding.whyPlay.subtitle"
            defaultMessage="The game has changed."
          />
        }
        chipTitle={
          <FormattedMessage
            id="landing.NewLanding.whyPlay.chip"
            defaultMessage="Why play?"
          />
        }
      >
        <WhyPlayContainer>
          {steps.map(step => (
            <StepByStep
              key={step.img.avif}
              img={step.img}
              titleImg={step.titleImg}
              title={step.title}
              subtitle={step.subtitle}
            />
          ))}
        </WhyPlayContainer>
        <Button color="secondary" size="medium" onClick={handleNavigate}>
          <FormattedMessage {...glossary.playNow} />
        </Button>
      </Section>
      <Section
        title={
          <FormattedMessage
            id="landing.NewLanding.Build.title"
            defaultMessage="Build your dream team"
          />
        }
        subtitle={
          <FormattedMessage
            id="landing.NewLanding.Build.subtitle"
            defaultMessage="Pick your ideal 5 player line ups each Game Week. Test your skills below."
          />
        }
        chipTitle={
          <FormattedMessage
            id="landing.NewLanding.Build.Chiptitle"
            defaultMessage="Prove your managerial skills"
          />
        }
      >
        <ErrorBoundary fallback={null}>
          <PveWidget handleNavigate={handleNavigate} />
        </ErrorBoundary>
        {/* KEEP FOR ITERATION WITH SCOUT SIMULATOR */}
        {/* <Button color="secondary" size="medium" onClick={signUp}>
          <FormattedMessage
            id="football.newLanding.ProveYourSkills.CTA"
            defaultMessage="Create my club"
          />
        </Button> */}
      </Section>
      <WinBigSection
        chipTitle={
          <FormattedMessage
            id="landing.NewLanding.BigWins.chipTitle"
            defaultMessage="Big wins. Twice a week."
          />
        }
        title={
          <FormattedMessage
            id="landing.NewLanding.BigWins.title"
            defaultMessage="Your ball knowledge is rewarded"
          />
        }
        subtitle={
          <FormattedMessage
            id="landing.NewLanding.BigWins.subTitle"
            defaultMessage="Play for more than just pride. This is fantasy football that rewards you."
          />
        }
      >
        <WinBigBackground src={bigWinBackground} alt="" />
        <Marquee direction="normal" duration="30s">
          <MarqueeContent>
            {marqueeContent.map(element => (
              <picture key={element.avif}>
                <source srcSet={element.avif} type="image/avif" />
                <MarqueeWinImage
                  src={element.pictureUrl}
                  fetchpriority="low"
                  alt=""
                />
              </picture>
            ))}
            {marqueeContent.map(element => (
              <picture key={element.avif}>
                <source srcSet={element.avif} type="image/avif" />
                <MarqueeWinImage
                  src={element.pictureUrl}
                  fetchpriority="low"
                  alt=""
                />
              </picture>
            ))}
          </MarqueeContent>
        </Marquee>
        <Button color="secondary" size="medium" onClick={signUp}>
          <FormattedMessage {...glossary.playNow} />
        </Button>
      </WinBigSection>
      <Section
        gap={0}
        chipTitle={
          <FormattedMessage
            id="landing.NewLanding.community.chipTitle"
            defaultMessage="Get involved in something big"
          />
        }
        title={
          <FormattedMessage
            id="landing.NewLanding.community.title"
            defaultMessage="Join the community"
          />
        }
        subtitle={
          <FormattedMessage
            id="landing.NewLanding.community.subTitle"
            defaultMessage="Be part of the revolution in fantasy football."
          />
        }
      >
        <CommunityBlock
          showSubtitle={false}
          leaderboards={leaderboards.map(leaderboardType => ({
            sport: Sport.FOOTBALL,
            leaderboardType,
          }))}
          title={
            <FormattedMessage
              id="landing.NewLanding.community.CommunityBlock.title"
              defaultMessage="Total number of Sorare Managers"
            />
          }
        />
        <Testimonials testimonies={testimonies} />
      </Section>
      <Section
        title={
          <FormattedMessage
            id="landing.NewLanding.Ambassadors.title"
            defaultMessage="Backed by the best"
          />
        }
      >
        <Ambassadors />
      </Section>
      <Footer
        title={
          <FormattedMessage
            id="landing.EarlyAccess.Footer.title"
            defaultMessage="Your new football home"
          />
        }
        assets={{ avif: footerBackgroundAvif, pictureUrl: footerBackground }}
      />
      {showFAQ && <FAQ />}
    </Root>
  );
};

export default NewLanding;
